<template>
  <div class="datagrid-wrapper" :class="wrapperClasses">
    <table class="datagrid" :class="tableClasses">
      <thead>
      <!-- Строка фильтров -->
      <tr v-if="hasFilters" class="filters">
        <!-- Чекбокс слева — пустая ячейка в строке фильтров -->
        <th v-if="showCheckboxCell" class="chb"/>

        <th
          v-for="col in columns"
          :key="'filter_' + col.id"
          :style="{ width: col.width }"
        >
          <VDataGridFilterV2
            v-if="col.filterable"
            :column="col"
            :value="filters[col.id]"
            @update:value="onFilterChange(col.id, $event)"
          />
        </th>

        <!-- Action-кнопка справа — пустая ячейка в строке фильтров -->
        <th v-if="showActionCell" class="chb"/>
      </tr>

      <!-- Заголовки колонок -->
      <tr class="headers">
        <!-- Чекбокс слева — «выбрать все» -->
        <th v-if="showCheckboxCell" class="chb" :class="{ selected: isAllSelected }">
          <button
            v-if="!singleSelect"
            class="chb-btn"
            @click="toggleSelectAll"
          >
            <VIcon material="check"/>
          </button>
        </th>

        <th
          v-for="col in columns"
          :key="'header_' + col.id"
          :style="getHeaderStyle(col)"
          :class="getHeaderClass(col)"
          @mouseenter="$core.tooltip.show($event.currentTarget, col.hint)"
          @mouseleave="$core.tooltip.hide()"
        >
          <div
            class="header-content"
            :class="{ 'sort-label-wrapper': col.sortable }"
            @click="col.sortable && toggleSort(col.id)"
          >
            <component
              v-if="col.labelComponent"
              :is="col.labelComponent"
            />
            <template v-else>
              {{ col.label }}
            </template>
          </div>
          <VIcon
            v-if="col.hint && col.hintIconHide === false"
            class="hint-icon"
            material="help_outline"
            theme="outlined"
          />

          <button
            v-if="col.sortable"
            class="sort-btn"
            @click="toggleSort(col.id)"
          >
            <VIcon :material="getSortIcon(col.id)"/>
          </button>
        </th>

        <!-- Action-кнопка справа — пустой заголовок -->
        <th v-if="showActionCell" class="chb"/>
      </tr>
      </thead>

      <!-- Скелетон загрузки -->
      <tbody v-if="isLoadingState && !hasData">
      <tr
        v-for="n in skeletonRowCount"
        :key="'skeleton-row-' + n"
        class="skeleton-row"
      >
        <td :colspan="columnsCount"/>
      </tr>
      </tbody>

      <!-- Пустое состояние -->
      <tbody v-else-if="!hasData && !isLoadingState">
      <tr class="empty">
        <td :colspan="columnsCount">
          <slot name="empty">
            Нет данных
          </slot>
        </td>
      </tr>
      </tbody>

      <!-- Данные с группировкой -->
      <template v-else-if="groupChildrenProperty && hasData">
        <tbody
          v-for="item in displayData"
          :key="item[rowKey]"
          class="group"
        >
        <!-- Заголовок группы -->
        <tr
          class="group-header"
          :data-row-identity="item[rowKey]"
          :class="{
              disabled:  isGroupDisabled(item, item[groupChildrenProperty]),
              clickable: isGroupClickable && !isGroupDisabled(item, item[groupChildrenProperty]),
            }"
          @click="onGroupClick(item, $event)"
          @dblclick="onGroupDblClick(item, $event)"
        >
          <!-- Чекбокс группы слева -->
          <td
            v-if="showCheckboxCell && !singleSelect"
            class="chb"
          >
            <input
              type="checkbox"
              :checked="isGroupSelected(item, item[groupChildrenProperty])"
              :disabled="isGroupDisabled(item, item[groupChildrenProperty])"
            />
            <button
              class="chb-btn"
              :disabled="isGroupDisabled(item, item[groupChildrenProperty])"
              @click.stop="toggleGroupSelect(item, item[groupChildrenProperty])"
            >
              <span class="material-icons mds-icon">check</span>
            </button>
          </td>

          <td :colspan="columns.length">
            <div class="group-content">
              <component
                v-if="groupHeaderSlot"
                :is="{ render: () => groupHeaderSlot({
                      parent: item,
                      children: item[groupChildrenProperty],
                      isDisabled: isGroupDisabled(item, item[groupChildrenProperty]),
                      isSelected: isSelected(null, item)
                    }) }"
              />

              <VKebabButton v-if="groupContextMenu">
                <VContextMenu
                  :items="groupContextMenu"
                  :row="item"
                  :on-action="refreshFromContextMenu"

                  @context-menu-item:done="onContextMenuItemDone"
                />
              </VKebabButton>
            </div>
          </td>

          <!-- Action-кнопка группы справа — пустая ячейка -->
          <td v-if="showActionCell" class="chb"/>
        </tr>

        <!-- Строки группы -->
        <VDataGridRowV2
          v-for="(row, index) in item[groupChildrenProperty]"
          :key="row[rowKey] ?? index"
          :row="row"
          :columns="columns"
          :selectable="selectable"
          :selection-control="selectionControl"
          :is-clickable="isClickable"
          :is-selected="isSelected(row)"
          :is-disabled="isRowDisabled(row, item)"
          :disabled-reason="getRowDisabledReason(row)"
          :draggable="draggableRows"
          @click="onRowClick(row, $event)"
          @dblclick="onRowDblClick(row, index, $event)"
          @contextmenu="(event, column) => onRowContextMenu(row, event, column)"
          @select="onRowSelect(row, item)"
          @drag:start="$emit('row:drag-start', $event)"
          @drop="$emit('row:drop', $event)"
        />

        <!-- Пустая группа -->
        <tr
          v-if="!item[groupChildrenProperty]?.length"
          class="empty"
        >
          <td :colspan="columnsCount">
            <slot name="empty">
              Нет данных
            </slot>
          </td>
        </tr>
        </tbody>
      </template>

      <!-- Данные без группировки -->
      <tbody v-else-if="hasData">
      <VDataGridRowV2
        v-for="(row, index) in displayData"
        :key="row[rowKey] ?? index"
        :row="row"
        :index="index"
        :columns="columns"
        :selectable="selectable"
        :selection-control="selectionControl"
        :is-clickable="isClickable"
        :is-selected="isSelected(row)"
        :is-disabled="isRowDisabled(row)"
        :disabled-reason="getRowDisabledReason(row)"
        :draggable="draggableRows"
        @click="onRowClick(row, $event)"
        @dblclick="onRowDblClick(row, index, $event)"
        @contextmenu="(event, column) => onRowContextMenu(row, event, column)"
        @select="onRowSelect(row)"
        @drag:start="$emit('row:drag-start', $event)"
        @drop="$emit('row:drop', $event)"
      />
      </tbody>

      <!-- Footer -->
      <tfoot v-if="hasFooter && hasData">
      <tr class="datagrid-footer">
        <td v-if="showCheckboxCell" class="chb"/>

        <td
          v-for="col in columns"
          :key="'footer_' + col.id"
          :style="{ width: col.width, textAlign: col.contentAlign }"
        >
          <component
            v-if="col.footerSlot"
            :is="{ render: col.footerSlot }"
          />
        </td>

        <td v-if="showActionCell" class="chb"/>
      </tr>
      </tfoot>

      <!-- Action Bar -->
      <tfoot v-if="actionBarSlot">
      <tr class="datagrid-actions-bar">
        <td :colspan="columnsCount">
          <div
            class="datagrid-actions-bar-content"
            :class="[actionBarProps?.class, { disabled: actionBarDisabled }]"
            @mouseenter="$core.tooltip.show($event.currentTarget, actionBarDisabledTitle ?? '')"
            @mouseleave="$core.tooltip.hide()"
          >
            <component
              :is="{ render: () => actionBarSlot({ selectedRows: selectedRowsArray }) }"
            />
          </div>
        </td>
      </tr>
      </tfoot>
    </table>

    <!-- Пагинация -->
    <VDataGridPaginationV2
      v-if="paginated && displayPagination.total > 0"
      :pagination="displayPagination"
      @update:pagination="onPaginationChange"
    />

    <!-- Контекстное меню -->
    <VDataGridContextMenuPopupV2
      v-if="isContextMenuVisible"
      :visible="contextMenuState.visible"
      :row="contextMenuState.row"
      @close="closeContextMenu"
    >
      <template #default="{ row, moveUp }">
        <VContextMenu
          v-if="contextMenuState.items !== null"
          :items="contextMenuState.items"
          :row="row"
          :on-action="refreshFromContextMenu"
          :selectable="selectable"
          :on-row-select="startSelectionWithRow"
          :cancel-selection="cancelSelection"
          :move-up="moveUp"

          @context-menu-item:done="onContextMenuItemDone"
        />
        <component
          v-else
          :is="{ render: () => contextMenuSlot({ row })}"
        />
      </template>
    </VDataGridContextMenuPopupV2>
  </div>
</template>

<style lang="scss" src="./VDataGridV2.scss"/>

<script lang="ts">

import debounce from 'lodash.debounce';
import VIcon from '@/lib/components/VIcon/VIcon.vue';
import VDataGridPaginationV2 from './VDataGridPaginationV2.vue';
import VDataGridRowV2 from './VDataGridRowV2.vue';
import VDataGridFilterV2 from './VDataGridFilterV2.vue';
import VDataGridContextMenuPopupV2 from './VDataGridContextMenuPopupV2.vue';
import VContextMenu from "@/lib/components/VContextMenu/VContextMenu.vue";
import VKebabButton from '@/lib/components/VKebabButton/VKebabButton.vue';

import type DataGridLoader from './DataGridLoader';
import type { UnwrapNestedRefs } from 'vue';
import type {DataGridColumn} from './VDataGridColumnV2.vue';
import ContextMenuItem from '@/lib/components/VContextMenu/ContextMenuItem';
import DataGridFiltersCacheService from "@/lib/components/VDataGridV2/DataGridFiltersCacheService";


type DataGridRow = Record<string, any>;

/** Состояние контекстного меню */
type ContextMenuState = {
  visible: boolean;
  row: Record<string, unknown> | null;
  /**
   * Пункты открытого меню: пункты строки (проп contextMenu), на ячейке с filter-by-value перед ними «Фильтровать по …».
   * null — меню строки задано слотом, пунктов нет, рисует слот.
   */
  items: ContextMenuItem[] | null;
};


export default defineComponent({
  name: 'VDataGridV2',

  components: {
    VIcon,
    VDataGridPaginationV2,
    VDataGridRowV2,
    VDataGridFilterV2,
    VDataGridContextMenuPopupV2,
    VContextMenu,
  },

  props: {
    /**
     * DataGridLoader — основной способ подключения данных.
     * Управляет загрузкой, пагинацией и внешними фильтрами.
     */
    loader: {
      type: Object as PropType<DataGridLoader | UnwrapNestedRefs<DataGridLoader>>,
      default: null,
    },

    /**
     * Локальные данные (без загрузки с сервера)
     */
    data: {
      type: Array as PropType<DataGridRow[]>,
      default: null,
    },

    /**
     * Свойство объекта группы, содержащее дочерние строки
     */
    groupChildrenProperty: {
      type: String as PropType<string>,
      default: null,
    },

    /**
     * Включить пагинацию
     */
    paginated: {
      type: Boolean,
      default: false,
    },

    /**
     * Использовать глобальный кэш фильтров
     */
    useFiltersCache: {
      type: Boolean,
      default: true,
    },

    /**
     * Тип сущности для namespace фильтров
     * Например: 'partner', 'order', 'product'
     * Обязателен если useFiltersCache = true
     */
    entityType: {
      type: String as PropType<string>,
      default: null,
      validator(value: string, props: Record<string, unknown>) {
        if (props.useFiltersCache && !value) {
          console.error('VDataGridV2: при use-filters-cache=true необходимо указать entity-type');
          return false;
        }
        return true;
      },
    },

    /**
     * Размер страницы по умолчанию
     */
    pageSize: {
      type: Number,
      default: 50,
    },

    /**
     * Включить выбор строк
     */
    selectable: {
      type: Boolean,
      default: false,
    },

    /**
     * Одиночный выбор
     */
    singleSelect: {
      type: Boolean,
      default: false,
    },

    /**
     * Выбранные строки (v-model)
     */
    selectedRows: {
      type: Array as PropType<DataGridRow[]>,
      default: () => [],
    },

    /**
     * Ключ идентификатора строки
     */
    rowKey: {
      type: String,
      default: 'id',
    },

    /**
     * Debounce для текстовых фильтров (мс)
     */
    filterDebounce: {
      type: Number,
      default: 300,
    },

    /**
     * Функция определения disabled состояния строки.
     * Возвращает строку-причину (станет подписью поверх disabled-строки) или boolean.
     */
    disableRowWhen: {
      type: Function as PropType<(row: any) => string | boolean>,
      default: null,
    },

    /**
     * Функция проверки disabled для родительских строк (групп)
     */
    disableGroupWhen: {
      type: Function as PropType<(parent: any, children: any[]) => boolean>,
      default: null,
    },

    /**
     * Количество скелетон-строк при загрузке
     */
    skeletonRowCount: {
      type: Number,
      default: 12,
    },

    /**
     * Включить зебру (чередование цвета строк)
     */
    striped: {
      type: Boolean,
      default: false,
    },

    /**
     * Режим контрола выбора строк:
     * - 'checkbox' — стандартный чекбокс слева
     * - 'action'   — кнопка add/delete справа
     * - 'none'     — без визуального контрола, выделение только кликом по строке
     */
    selectionControl: {
      type: String as PropType<'checkbox' | 'action' | 'none'>,
      default: 'action',
      validator: (v: string) => ['checkbox', 'action', 'none'].includes(v),
    },

    /**
     * Включить HTML5 drag-n-drop для сортировки строк.
     * При включении нужно слушать события row:drag-start и row:drop
     * снаружи и вызывать соответствующий Service.changeSort().
     */
    draggableRows: {
      type: Boolean,
      default: false,
    },

    /**
     * Раскладка таблицы в контейнере:
     * - 'auto' — ширина по содержимому, таблица центрируется в контейнере (по умолчанию).
     *            Не разреживает колонки на широких мониторах, оставляет воздух по бокам.
     * - 'fill' — растянуть на всю ширину контейнера.
     *            Имеет смысл, когда есть колонка-абсорбер с длинным текстом
     *            (комментарий, описание, наименование).
     * - 'left' — ширина по содержимому, прижата к левому краю.
     *            Для случаев с боковой панелью/деталкой справа.
     */
    layout: {
      type: String as PropType<'fill' | 'auto' | 'left'>,
      default: 'auto',
      validator: (v: string) => ['fill', 'auto', 'left'].includes(v),
    },

    /**
     * Доменное контекстное меню одним пропом.
     *
     * Принимает массив ContextMenuItem (обычно геттер сервиса домена,
     * например AssembliesService.contextMenu). При указании грид:
     * - добавляет в конец синтетическую колонку с кебаб-кнопкой;
     * - показывает то же меню по правому клику на строке;
     * - после действия пункта сам обновляет данные (refresh), только если
     *   callback пункта вернул ровно true.
     *
     * Имеет приоритет над ручным <VDataGridContextMenuV2> в слоте:
     * если передан проп — ручной слот меню игнорируется.
     */
    contextMenu: {
      type: Array as PropType<ContextMenuItem[] | null>,
      default: null,
    },
    /**
     * Контекстное меню для группирующей строки в двухуровневом реестре.
     * Аналогично пропу :context-menu, но пункты применяются к родительской
     * сущности группы. Рендерится кебаб-кнопкой в правом конце шапки группы.
     *
     * Имеет смысл только при groupChildrenProperty !== null.
     */
    groupContextMenu: {
      type:    Array as PropType<ContextMenuItem[]>,
      default: null,
    },
  },

  emits: [
    'update:selectedRows',
    'update:selectable',
    'selectedRows:update',
    'row:click',
    'row:dblclick',
    'row:contextmenu',
    'row:selected',
    'row:deselected',
    'row:drag-start',
    'row:drop',
    'group:click',
    'group:dblclick',
    'contextMenuItem:done',
  ],

  data() {
    return {
      localData: [] as DataGridRow[],
      columns: [] as DataGridColumn[],

      /**
       * Состав колонок на момент последнего parseSlot — id в порядке следования
       * плюс признак filterable. Сверяется после каждого ререндера, чтобы поймать
       * v-if на VDataGridColumnV2 и смену :filterable.
       */
      slotSignature: '',

      groupHeaderSlot: null as ((props: {
        parent: DataGridRow;
        children: DataGridRow[]
      }) => VNode[]) | null,
      actionBarSlot: null as ((props: {
        selectedRows: DataGridRow[]
      }) => VNode[]) | null,
      actionBarProps: null as { disabled?: string | boolean | null; class?: unknown } | null,
      contextMenuSlot: null as ((props: { row: Record<string, unknown> }) => VNode[]) | null,

      filters: {} as Record<string, unknown>,

      sort: {
        by: null as string | null,
        direction: null as 'asc' | 'desc' | null,
      },

      pagination: {
        limit: this.pageSize,
        offset: 0,
        total: 0,
      },

      localSelectedRows: new Map<string | number, DataGridRow>(),

      contextMenuState: {
        visible: false,
        row: null,
        items: null,
      } as ContextMenuState,

      debouncedLoadData: null as (() => void) | null,
    };
  },

  watch: {
    selectedRows: {
      handler(val: DataGridRow[]) {
        const map = new Map<string | number, DataGridRow>();

        for (const row of val) {
          const key = row[this.rowKey] as string | number;
          map.set(key, row);
        }

        this.localSelectedRows = map;
      },
      immediate: true,
      deep: true,
    },

    data: {
      handler(val: DataGridRow[]) {
        this.localData = val || [];
      },
      immediate: true,
      deep: true,
    },

    /**
     * При переключении selectable нужно перепарсить колонки — кебаб-колонка
     * контекстного меню добавляется/удаляется в applyContextMenuProp /
     * parseContextMenuFromSlot, и без переразбора слота она зависнет
     * в неактуальном состоянии.
     */
    selectable() {
      this.parseSlot();
    },
  },

  created() {
    this.debouncedLoadData = debounce(() => {
      this.triggerLoad();
    }, this.filterDebounce);
  },

  mounted() {
    this.parseSlot();
    this.initFilters();
    this.initSort();

    if (this.loader) {
      this.loader.onExternalParamsChange(this.onExternalParamsChange);

      this.applyExternalFilter();
      this.triggerLoad();
    }
  },

  beforeUnmount() {
    this.loader?.destroy();
  },

  /**
   * Колонки собираются императивно в parseSlot, поэтому Vue сам не знает,
   * что состав слота изменился: v-if на VDataGridColumnV2 добавит или уберёт
   * vnode, а this.columns останется прежним. Раньше потребители лечили это
   * вручную — watch на своё условие, nextTick и вызов parseSlot через $refs.
   *
   * Здесь после каждого ререндера сверяется сигнатура состава (id колонок
   * в порядке следования плюс их filterable). Совпала — ничего не делаем,
   * это обычная перерисовка данных. Не совпала — переразбираем слот
   * и пересобираем фильтры, чтобы у новой колонки появился свой ключ
   * в this.filters.
   *
   * Сортировка намеренно не переинициализируется: initSort перетёр бы
   * выбранную пользователем сортировку значением из кеша или дефолтом колонки.
   *
   * Рекурсии нет: parseSlot меняет columns, это вызывает ещё один ререндер,
   * но на нём сигнатура уже совпадёт и цикл остановится.
   */
  updated() {
    const vnodes = this.$slots.default?.() || [];

    if (this.buildSlotSignature(vnodes) !== this.slotSignature) {
      this.parseSlot(vnodes);
      this.initFilters();
    }
  },

  computed: {
    /**
     * Класс disabled на action-bar — true если `disabled` пропом передан
     * не-falsy (строка или true). Вынесено в computed чтобы Vue кешировал
     * результат — шаблон таблицы перерисовывается часто (на каждое изменение
     * данных/выделений), пересчитывать эти проверки на каждом ререндере смысла нет.
     */
    actionBarDisabled(): boolean {
      return !!this.actionBarProps?.disabled;
    },

    /**
     * Тултип с причиной — только если `disabled` передан строкой.
     * Показывается через $core.tooltip сразу при наведении, а не нативным title с задержкой.
     * Если true или null — тултипа нет.
     */
    actionBarDisabledTitle(): string | null {
      const d = this.actionBarProps?.disabled;
      if (typeof d === 'string') {
        return d;
      }
      return null;
    },

    /**
     * Данные для отображения: из loader или из localData
     */
    displayData(): DataGridRow[] {
      if (this.loader) {
        return this.loader.data as DataGridRow[];
      }
      return this.localData;
    },

    /**
     * Пагинация для VDataGridPaginationV2
     */
    displayPagination(): { limit: number; offset: number; total: number } {
      if (this.loader) {
        return {
          limit: this.loader.pagination.limit,
          offset: this.loader.pagination.offset,
          total: this.loader.pagination.total,
        };
      }
      return {
        limit: this.pagination.limit,
        offset: this.pagination.offset,
        total: this.pagination.total,
      };
    },

    /**
     * Состояние загрузки: из loader или из localData
     */
    isLoadingState(): boolean {
      if (this.loader) {
        return this.loader.isLoading;
      }
      return false;
    },

    tableClasses(): Record<string, boolean> {
      return {
        selectable: this.selectable,
        striped: this.striped && !this.groupChildrenProperty,
      };
    },

    /**
     * Классы layout вешаются на .datagrid-wrapper, а не на <table>.
     * Обёртка диктует ширину контейнера (вместе с пагинацией внутри),
     * таблица всегда занимает 100% обёртки.
     *
     * - 'auto' — без классов, обёртка по содержимому таблицы и центрируется.
     * - 'fill' — обёртка во всю ширину родителя.
     * - 'left' — по содержимому, прижата влево.
     */
    wrapperClasses(): Record<string, boolean> {
      return {
        fill: this.layout === 'fill',
        left: this.layout === 'left',
      };
    },

    hasFilters(): boolean {
      return this.columns.some(col => col.filterable);
    },

    hasData(): boolean {
      return this.$core.utils.isNotEmptyArray(this.displayData);
    },

    hasFooter(): boolean {
      return this.columns.some(col => col.footerSlot !== null);
    },

    columnsCount(): number {
      return this.columns.length + (this.showSelectCell ? 1 : 0);
    },

    selectedRowsArray(): DataGridRow[] {
      return Array.from(this.localSelectedRows.values());
    },

    isAllSelected(): boolean {
      if (this.localSelectedRows.size === 0) return false;

      const selectableRows = this.getSelectableRows();

      return selectableRows.length > 0 &&
        selectableRows.every(row => this.localSelectedRows.has(row[this.rowKey] as string | number));
    },

    isClickable(): boolean {
      return Boolean(this.$.vnode.props?.['onRow:dblclick'])
    },
    isGroupClickable(): boolean {
      return Boolean(this.$.vnode.props?.['onGroup:dblclick'])
    },
    /** Чекбокс выбора — отображается слева */
    showCheckboxCell(): boolean {
      return this.selectable && this.selectionControl === 'checkbox';
    },
    /** Кнопка добавления/удаления — отображается справа */
    showActionCell(): boolean {
      return this.selectable && this.selectionControl === 'action';
    },
    /** Любой контрол выбора (для подсчёта colspan) */
    showSelectCell(): boolean {
      return this.showCheckboxCell || this.showActionCell
    },
    /**
     * Контекстное меню скрывается автоматически в режиме выбора (selectable).
     * Логика: когда пользователь занимается выбором строк, действия со строками
     * через меню (открыть, удалить и т.д.) — это другой сценарий, его смешивание
     * с выбором путает пользователя.
     */
    isContextMenuVisible(): boolean {
      return this.selectable === false;
    },
  },


  methods: {
    // ==================== Загрузка данных ====================

    /**
     * Единая точка входа для загрузки данных.
     * Передаёт лоадеру извлечённые фильтры, сортировку и пагинацию.
     */
    triggerLoad(): void {
      if (!this.loader) return;

      this.loader.onParamsChange(
        this.buildFilters(),
        this.sort.by ? {by: this.sort.by, direction: this.sort.direction || 'asc'} : null,
        this.paginated ? {limit: this.pagination.limit, offset: this.pagination.offset} : null,
        /** filtersRaw — фильтры как в контролах шапки (объекты целиком), см. onParamsChange лоадера */
        this.filters,
      );

      /** Созависимые фильтры: filter-props одного считаются от значения другого
       *  через loader.filters (варианты «Изменений» в ChangelogsRegistry зависят
       *  от «Типа объекта»). Лоадер только что получил новые значения — пересчитываем */
      this.syncFilterProps();
    },

    onExternalParamsChange(): void {
      this.applyExternalFilter();
      this.resetPaginationOffset();
      this.triggerLoad();
    },

    /**
     * Кладёт значения фильтра страницы (useExternalFilter лоадера) в фильтры колонок
     * с тем же ключом (filter-send-name или id). Поле у фильтра есть, а в его параметрах
     * нет — его сбросили, колонку очищаем. Колонки, которых у фильтра нет (товар), не трогаем.
     */
    applyExternalFilter(): void {
      const filter = this.loader?.getExternalFilter() ?? null;

      if (filter === null) {
        return;
      }

      const params = filter.getRequestParams();

      for (const col of this.columns) {
        const key = col.filterSendName || col.id;

        if (col.filterable === true && key in filter) {
          const value = params[key] ?? null;

          this.filters[col.id] = value;

          if (this.useFiltersCache && this.entityType) {
            DataGridFiltersCacheService.setFilter(this.entityType, col.id, value);
          }
        }
      }
    },

    // ==================== Парсинг слота ====================

    /**
     * Разбирает слот по умолчанию в колонки и служебные слоты.
     *
     * vnodes передаются параметром из updated — там они уже получены для
     * сверки сигнатуры, второй вызов слота не нужен. Внешние вызовы
     * (mounted, watch selectable, потребители через $refs) идут без аргумента.
     */
    parseSlot(vnodes: VNode[] = this.$slots.default?.() || []): void {
      this.columns = [];

      this.groupHeaderSlot = null;
      this.actionBarSlot = null;
      this.actionBarProps = null;
      this.contextMenuSlot = null;

      this.extractComponents(vnodes);

      this.applyContextMenuProp();

      this.slotSignature = this.buildSlotSignature(vnodes);
    },

    /**
     * Сигнатура состава колонок — id в порядке следования плюс признак
     * filterable: `:filterable="условие"` не меняет состав, но требует
     * переразбора — иначе фильтр колонки застынет в прежнем состоянии.
     *
     * Остальные props в сигнатуру не входят намеренно: если понадобится
     * отслеживать ещё и label, width, sortable — это уже сравнение props
     * целиком, другой механизм, а не дописывание полей сюда.
     *
     * Считается по vnodes, а не по this.columns: кебаб-колонка из пропа
     * :context-menu в слоте отсутствует, и сигнатуры из разных источников
     * никогда бы не совпали — грид перепарсивался бы на каждом ререндере.
     */
    buildSlotSignature(vnodes: VNode[]): string {
      return this.collectColumnVnodes(vnodes)
        .map(vnode => {
          const props = (vnode.props || {}) as Record<string, unknown>;
          const filterable = 'filterable' in props && props.filterable !== false;

          return `${props.id}:${filterable}`;
        })
        .join('|');
    },

    /** Vnode колонок VDataGridColumnV2 из слота, включая вложенные во фрагменты. Общий обход для buildSlotSignature и syncFilterProps */
    collectColumnVnodes(vnodes: VNode[], result: VNode[] = []): VNode[] {
      for (const vnode of vnodes) {
        if (!vnode.type) continue;

        if ((vnode.type as { name?: string }).name === 'VDataGridColumnV2') {
          result.push(vnode);
        }

        if (Array.isArray(vnode.children)) {
          this.collectColumnVnodes(vnode.children as VNode[], result);
        }
      }

      return result;
    },

    /**
     * Обновляет filter-props колонок для созависимых фильтров: parseColumn хранит их снимком,
     * а варианты одного фильтра могут зависеть от значения другого («Изменения» от «Типа объекта»
     * в ChangelogsRegistry). Зовётся из triggerLoad, когда значения фильтров ушли в лоадер.
     */
    syncFilterProps(): void {
      const vnodes = this.$slots.default?.() || [];

      for (const vnode of this.collectColumnVnodes(vnodes)) {
        const props = (vnode.props || {}) as Record<string, unknown>;
        const col = this.columns.find(_col => _col.id === props.id);

        if (col === undefined) {
          // Колонки нет среди разобранных — состав сверен сигнатурой, сюда не попадаем
        }
        else {
          col.filterProps = (props['filter-props'] || props.filterProps || {}) as Record<string, unknown>;
        }
      }
    },

    /**
     * Если передан проп :context-menu — разворачиваем его в кебаб-колонку
     * и в меню по правому клику. Проп имеет приоритет над ручным
     * <VDataGridContextMenuV2> в слоте: если задан и то и другое — ручной
     * слот игнорируется, чтобы не показать два меню.
     */
    applyContextMenuProp(): void {
      if (this.isContextMenuVisible === false) {
        this.contextMenuSlot = null;
        return;
      }

      if (!this.contextMenu) {
        return;
      }

      if (this.contextMenuSlot) {
        console.warn(
          'VDataGridV2: одновременно заданы проп :context-menu и слот ' +
          '<VDataGridContextMenuV2>. Используется проп, слот игнорируется.'
        );
        this.contextMenuSlot = null;
      }

      this.columns.push(this.buildKebabColumn());
    },

    /**
     * Кебаб-колонка из пропа :context-menu — рендерит VContextMenu по item'ам.
     */
    buildKebabColumn(): DataGridColumn {
      const cellSlot = (slotProps: { row: Record<string, unknown> }): VNode[] => {
        const row = slotProps.row;

        const ContextMenuNode = h(VContextMenu, {
          items: this.contextMenu,
          row,
          onAction: this.refreshFromContextMenu,
          selectable: this.selectable,
          onRowSelect: this.startSelectionWithRow,
          cancelSelection: this.cancelSelection,

          /** Слушатель события contextMenuItem:done — в h() имя обработчика пишется руками */
          'onContextMenuItem:done': this.onContextMenuItemDone,
        });

        return [
          h(VKebabButton, null, {
            default: () => ContextMenuNode,
          }),
        ];
      };

      return this.buildContextMenuColumn(cellSlot);
    },

    /**
     * Кебаб-колонка из слота <VDataGridContextMenuV2> — рендерит scoped-слот пользователя.
     */
    parseContextMenuFromSlot(): DataGridColumn {
      const cellSlot = (slotProps: { row: Record<string, unknown> }): VNode[] => {
        const row = slotProps.row;

        return [
          h(VKebabButton, null, {
            default: () => this.contextMenuSlot?.({ row }) ?? [],
          }),
        ];
      };

      return this.buildContextMenuColumn(cellSlot);
    },

    extractComponents(vnodes: VNode[]): void {
      for (const vnode of vnodes) {
        if (!vnode.type) continue;

        const name = (vnode.type as { name?: string }).name;

        switch (name) {
          case 'VDataGridColumnV2':
            this.columns.push(this.parseColumn(vnode));
            break;
          case 'VDataGridGroupV2':
            this.groupHeaderSlot = (vnode.children as { default?: () => VNode[] })?.default || null;
            break;
          case 'VDataGridActionBarV2':
            this.actionBarSlot = (vnode.children as { default?: () => VNode[] })?.default || null;
            this.actionBarProps = (vnode.props || {}) as {
              disabled?: string | boolean | null;
              class?: unknown
            };
            break;
          case 'VDataGridContextMenuV2':
            // В режиме выбора меню скрывается — игнорируем слот.
            if (this.isContextMenuVisible === false) break;
            this.contextMenuSlot = (vnode.children as { default?: () => VNode[] })?.default || null;
            this.columns.push(this.parseContextMenuFromSlot());

            break;
        }

        if (Array.isArray(vnode.children)) {
          this.extractComponents(vnode.children as VNode[]);
        }
      }
    },

    /** Обновляет данные. Зовётся из VContextMenu, если callback пункта вернул true */
    refreshFromContextMenu(): void {
      this.refresh();
    },

    /** Пробрасывает выполнение пункта меню наверх вместе с результатом */
    onContextMenuItemDone(item: ContextMenuItem, row: Record<string, unknown>, result: boolean | void): void {
      this.$emit('contextMenuItem:done', item, row, result);
    },

    parseColumn(vnode: VNode): DataGridColumn {
      const props = (vnode.props || {}) as Record<string, unknown>;
      const slots = (vnode.children || {}) as {
        label?: () => VNode[];
        default?: () => VNode[];
        footer?: () => VNode[]
      };

      const sortDefaultValue = props['sort-default-value'] || props.sortDefaultValue;
      const filterIdentity = props['filter-identity'] || props.filterIdentity || null;

      return {
        id: props.id as string,
        label: (props.label as string) || '',
        width: (props.width as string) || null,
        contentAlign: (props['content-align'] || props.contentAlign || 'left') as 'left' | 'center' | 'right',
        contentClass: (props['content-class'] || props.contentClass || null) as DataGridColumn['contentClass'],
        headerClass: (props['header-class'] || props.headerClass || null) as DataGridColumn['headerClass'],

        sortable: 'sortable' in props,
        sortDefaultValue: (sortDefaultValue || null) as string,

        hint: (props.hint as string) || null,
        hintIconHide: (props['hint-icon-hide'] ?? props.hintIconHide ?? false) as boolean,

        filterable: 'filterable' in props && props.filterable !== false,

        filterType: (props['filter-type'] || props.filterType || 'text') as DataGridColumn['filterType'],
        filterProps: (props['filter-props'] || props.filterProps || {}) as Record<string, unknown>,
        filterDefaultValue: props['filter-default-value'] ?? props.filterDefaultValue ?? null,
        filterSendName: (props['filter-send-name'] || props.filterSendName || null) as string | null,
        filterIdentity: typeof filterIdentity === 'string' && filterIdentity.includes(',')
          ? filterIdentity.split(',').map((s: string) => s.trim())
          : filterIdentity as string | null,


        labelSlot: slots.label || null,
        /**
         * Стабильный компонент label-слота. Создаётся ОДИН раз здесь, при парсинге
         * колонки. Инлайновый { render: col.labelSlot } в шаблоне пересоздавался
         * на каждом ререндере грида — Vue считал объект новым компонентом и
         * перемонтировал DOM заголовка, ломая сохранённые ссылки на элементы
         * (якоря попапов меню шапки). markRaw — чтобы Vue не оборачивал
         * определение компонента в реактивный прокси.
         */
        labelComponent: slots.label ? markRaw({ render: slots.label }) : null,
        cellSlot: slots.default || null,
        footerSlot: slots.footer || null,

        onEdit: (props.onEdit as DataGridColumn['onEdit']) || null,
        isFilterableByValue: ('filter-by-value' in props && props['filter-by-value'] !== false)
          || ('filterByValue' in props && props.filterByValue !== false),
        editMaterial: (props['edit-material'] || props.editMaterial || 'edit') as string,
      };
    },

    // ==================== Инициализация фильтров ====================

    initFilters(): void {
      const newFilters: Record<string, unknown> = {};

      for (const col of this.columns) {
        if (col.filterable) {
          /** Начальное значение фильтра: сначала дефолт колонки (filter-default-value),
           *  ниже его перекроет значение из кэша, если оно там есть */
          let defaultValue = col.filterDefaultValue ?? null;

          if (this.useFiltersCache && this.entityType) {
            let cachedValue = DataGridFiltersCacheService.getFilter(this.entityType, col.id);

            /**
             * Диапазон дат без обеих границ — фильтр не задан. В кэш такое
             * значение попадало как { dateFrom: null, dateTo: null }, проверку
             * на null ниже проходит и уходило бы на сервер пустым диапазоном.
             */
            if (col.filterType === 'date-range' && cachedValue !== null) {
              const range = cachedValue as { dateFrom?: unknown; dateTo?: unknown };

              const isEmptyRange = (range.dateFrom === null || range.dateFrom === undefined)
                                && (range.dateTo === null || range.dateTo === undefined);

              if (isEmptyRange === true) {
                cachedValue = null;
              }
            }

            if (cachedValue !== null) {
              defaultValue = cachedValue;
            }
          }

          /** Значение, которое уже стоит в фильтре, приоритетнее и кэша, и дефолта
           *  (initFilters зовётся повторно из updated при смене состава колонок) */
          newFilters[col.id] = this.filters[col.id] ?? defaultValue;
        }
      }

      this.filters = newFilters;
    },

    // ==================== Инициализация сортировки ====================

    initSort(): void {
      if (this.useFiltersCache && this.entityType) {
        const cachedSort = DataGridFiltersCacheService.getSort(this.entityType);

        if (cachedSort) {
          const isColumnExists = this.columns.some(col => col.id === cachedSort.by && col.sortable);

          if (isColumnExists) {
            this.sort = {
              by: cachedSort.by,
              direction: cachedSort.direction,
            };
            return;
          }
        }
      }

      const defaultSortColumn = this.columns.find(col => col.sortable && col.sortDefaultValue);

      if (defaultSortColumn) {
        this.sort = {
          by: defaultSortColumn.id,
          direction: defaultSortColumn.sortDefaultValue as 'asc' | 'desc',
        };
      }
    },

    // ==================== Сборка параметров ====================

    /** Собирает фильтры из состояния колонок */
    buildFilters(): Record<string, unknown> {
      const filters: Record<string, unknown> = {};

      for (const col of this.columns) {
        if (!col.filterable) continue;

        const value = this.filters[col.id];
        if (value === null || value === undefined || value === '') continue;

        const extracted = this.extractFilterValue(col, value);

        /**
         * Если указан filter-identity — извлекаем поля из опции и разворачиваем в корень payload.
         * filter-send-name в этом случае игнорируется.
         * filter-identity — массив полей: разворачиваем в корень, filter-send-name игнорируется
         */
        if (col.filterIdentity === '*') {
          // Весь объект опции целиком, без извлечения поля — под filterSendName || id
          filters[col.filterSendName || col.id] = value;
        } else if (Array.isArray(col.filterIdentity)) {
          // Массив полей — разворачиваем в корень, filter-send-name игнорируется
          if (extracted !== null && typeof extracted === 'object' && !Array.isArray(extracted)) {
            Object.assign(filters, extracted);
          }
        } else if (col.filterIdentity) {
          // Строка — filterIdentity само является ключом в payload
          filters[col.filterIdentity] = extracted;
        } else {
          // Не указан — используем filterSendName || id
          filters[col.filterSendName || col.id] = extracted;
        }
      }

      return filters;
    },

    /** Извлекает значение фильтра в зависимости от типа колонки */
    extractFilterValue(col: DataGridColumn, value: unknown): unknown {
      if (col.filterType === 'select') {
        return this.extractSelectFilterValue(col, value);
      } else {
        return value;
      }
    },

    /**
     * Извлекает значение(я) из объекта опции select-фильтра для отправки на сервер.
     *
     * Поведение зависит от типа filterIdentity колонки (проп filter-identity):
     *
     * - Строка (например, filter-identity="isVerified"):
     *   Возвращает примитив — значение указанного поля.
     *   Пример: { id: 1, isVerified: true } → true
     *
     * - Массив строк (например, filter-identity="isShipped,isShippedEntirely"):
     *   Возвращает объект только из указанных полей, которые есть в опции.
     *   Поля отсутствующие в опции игнорируются.
     *   Пример: { id: 1, isShipped: true, isShippedEntirely: true } → { isShipped: true, isShippedEntirely: true }
     *   При этом в buildFilters такой объект разворачивается в корень payload,
     *   а не вкладывается под ключ колонки.
     *
     * - Не указан (по умолчанию 'id'):
     *   Возвращает значение поля id.
     *   Пример: { id: 2, name: 'Частично' } → 2
     *
     * Важно: filterIdentity не передаётся в VSelect — он используется только внутри VDataGridV2.
     * Для идентификации опции внутри VSelect используется filterProps.optionIdentity.
     */
    extractOptionValue(
      value: unknown,
      identity: string | string[]
    ): unknown {
      if (value === null || typeof value !== 'object') return value;

      const option = value as Record<string, unknown>;

      if (Array.isArray(identity)) {
        return identity.reduce((extracted, key) => {
          if (key in option) {
            extracted[key] = option[key];
          }
          return extracted;
        }, {} as Record<string, unknown>);
      }

      return option[identity];
    },

    /**
     * Select-фильтр: извлекает значение(я) из опции через extractOptionValue.
     * Использует filterIdentity колонки (проп filter-identity).
     * Поддерживает одиночный и множественный выбор.
     */
    extractSelectFilterValue(col: DataGridColumn, value: unknown): unknown {
      const identity = col.filterIdentity || 'id';

      if (Array.isArray(value)) {
        return value.map(v => this.extractOptionValue(v, identity));
      } else {
        return this.extractOptionValue(value, identity);
      }
    },

    // ==================== Фильтры ====================

    onFilterChange(columnId: string, value: unknown): void {
      this.filters[columnId] = value;

      /** Новый фильтр — новая выборка: возвращаемся на первую страницу */
      this.resetPaginationOffset();

      if (this.useFiltersCache && this.entityType) {
        DataGridFiltersCacheService.setFilter(this.entityType, columnId, value);
      }


      if (this.loader) {
        const col = this.columns.find(c => c.id === columnId);

        if (col?.filterType === 'text') {
          this.debouncedLoadData?.();
        } else {
          this.triggerLoad();
        }
      }
    },

    // ==================== Сортировка ====================

    toggleSort(columnId: string): void {
      if (this.sort.by !== columnId) {
        this.sort.by = columnId;
        this.sort.direction = 'asc';
      } else if (this.sort.direction === 'asc') {
        this.sort.direction = 'desc';
      } else {
        this.sort.by = null;
        this.sort.direction = null;
      }

      if (this.useFiltersCache && this.entityType) {
        DataGridFiltersCacheService.setSort(
          this.entityType,
          this.sort.by,
          this.sort.direction
        );
      }

      this.resetPaginationOffset();

      if (this.loader) {
        this.triggerLoad();
      }
    },

    getSortIcon(columnId: string): string {
      if (this.sort.by !== columnId) return 'unfold_more';
      return this.sort.direction === 'asc' ? 'expand_less' : 'expand_more';
    },

    // ==================== Пагинация ====================

    onPaginationChange(pagination: { limit: number; offset: number }): void {
      this.pagination.limit = pagination.limit;
      this.pagination.offset = pagination.offset;

      if (this.loader) {
        this.triggerLoad();
      }
    },

    resetPaginationOffset(): void {
      this.pagination.offset = 0;
    },

    // ==================== Публичные методы ====================

    refresh(clearPaginationOffset: boolean = false): void {
      if (clearPaginationOffset) this.pagination.offset = 0;

      if (this.loader) {
        this.loader.refresh();
      }
    },

    updatePagination(pagination: { limit?: number; offset?: number; total?: number }): void {
      if (pagination.limit !== undefined) {
        this.pagination.limit = pagination.limit;
      }
      if (pagination.offset !== undefined) {
        this.pagination.offset = pagination.offset;
      }
      if (pagination.total !== undefined) {
        this.pagination.total = pagination.total;
      }
    },

    clearFilters(): void {
      for (const col of this.columns) {
        if (col.filterable) {
          this.filters[col.id] = col.filterDefaultValue ?? null;

          if (this.useFiltersCache && this.entityType) {
            DataGridFiltersCacheService.clearFilter(this.entityType, col.id);
          }
        }
      }

      this.sort = {by: null, direction: null};

      if (this.useFiltersCache && this.entityType) {
        DataGridFiltersCacheService.clearSort(this.entityType);
      }

      this.resetPaginationOffset();

      this.triggerLoad(true);
    },

    // ==================== Выбор строк ====================

    getAllRows(): Record<string, unknown>[] {
      if (this.groupChildrenProperty) {
        return this.displayData.flatMap(item =>
          (item[this.groupChildrenProperty] as Record<string, unknown>[]) || []
        );
      }
      return this.displayData;
    },

    isSelected(row: Record<string, unknown>, parent?: Record<string, unknown>): boolean {
      if (this.groupChildrenProperty && parent) {
        return parent[this.groupChildrenProperty].every(_row =>
          this.localSelectedRows.has(_row[this.rowKey] as string | number)
        )
      }

      return this.localSelectedRows.has(row[this.rowKey] as string | number);
    },

    isRowDisabled(row: Record<string, unknown>, parent?: Record<string, unknown>): boolean {
      if (parent && this.disableGroupWhen && this.disableGroupWhen(parent, [])) {
        return true;
      }

      if (!this.disableRowWhen) return false;

      const reason = this.disableRowWhen(row);

      if (reason === false) {
        return false;
      } else {
        return true;
      }
    },

    /** Причина disabled строки для подписи поверх строки. Пустая строка — причины нет. */
    getRowDisabledReason(row: Record<string, unknown>): string {
      if (!this.disableRowWhen) {
        return '';
      } else {
        const result = this.disableRowWhen(row);
        if (typeof result === 'string') {
          return result;
        } else {
          return '';
        }
      }
    },

    isGroupDisabled(parent: Record<string, unknown>, children: Record<string, unknown>[]): boolean {
      if (!this.disableGroupWhen) return false;
      return this.disableGroupWhen(parent, children);
    },

    onRowSelect(row: Record<string, unknown>, parent?: Record<string, unknown>): void {
      if (this.isRowDisabled(row, parent)) {
        return;
      }

      if (this.singleSelect) {
        this.localSelectedRows.clear();
      }

      const key = row[this.rowKey] as string | number;

      if (this.localSelectedRows.has(key)) {
        this.deselectRow(row);
      } else {
        this.selectRow(row);
      }

      this.emitSelectedRows();
    },

    isGroupSelected(parent: Record<string, unknown>, children: Record<string, unknown>[]): boolean {
      const selectableChildren = children.filter(child => !this.isRowDisabled(child, parent));

      if (selectableChildren.length === 0) {
        return false;
      }

      return selectableChildren.every(child =>
        this.localSelectedRows.has(child[this.rowKey] as string | number)
      );
    },

    toggleSelectAll(): void {
      const selectableRows = this.getSelectableRows();

      if (this.isAllSelected) {
        selectableRows.forEach(row => this.deselectRow(row));
      } else {
        this.localSelectedRows.forEach(row => this.deselectRow(row));
        selectableRows.forEach(row => this.selectRow(row));
      }

      this.emitSelectedRows();
    },

    toggleGroupSelect(parent: Record<string, unknown>, children: Record<string, unknown>[]): void {
      if (this.isGroupDisabled(parent, children)) {
        return;
      }

      const selectableChildren = children.filter(child => !this.isRowDisabled(child, parent));

      if (selectableChildren.length === 0) {
        return;
      }

      const isAllSelected = selectableChildren.every(child =>
        this.localSelectedRows.has(child[this.rowKey] as string | number)
      );

      if (isAllSelected) {
        selectableChildren.forEach(child => this.deselectRow(child));
      } else {
        selectableChildren.forEach(child => this.selectRow(child));
      }

      this.emitSelectedRows();
    },

    getSelectableRows(): Record<string, unknown>[] {
      const allRows = this.getAllRows();

      return allRows.filter(row => {
        if (this.groupChildrenProperty) {
          const parent = this.displayData.find(item =>
            (item[this.groupChildrenProperty] as Record<string, unknown>[])?.includes(row)
          );
          return !this.isRowDisabled(row, parent);
        }

        return !this.isRowDisabled(row);
      });
    },

    emitSelectedRows(): void {
      const rows = this.selectedRowsArray;
      this.$emit('update:selectedRows', rows);
      this.$emit('selectedRows:update', rows);
    },

    // ==================== События строк ====================

    onRowClick(row: Record<string, unknown>, event: MouseEvent): void {
      this.$emit('row:click', row, event);
    },

    onRowDblClick(row: Record<string, unknown>, index: number, event: MouseEvent): void {
      if (this.selectable) {
        return;
      }
      this.$emit('row:dblclick', row, index, event);
    },

    /** column — колонка ячейки, по которой кликнули; null — клик по чекбоксу или кнопке выбора: их рисует строка, колонок у них нет */
    onRowContextMenu(row: Record<string, unknown>, event: MouseEvent, column: DataGridColumn | null = null): void {
      event.preventDefault();

      if (this.isContextMenuVisible === false) {
        // в режиме выбора меню не показываем
      }
      else {
        /** Пункты строки; null — меню строки задано слотом */
        let items = this.contextMenu;

        /** Под курсором рисуется первый пункт сервисного контекстного меню, над ним все служебные («Фильтровать по …») */
        if (column !== null && column.isFilterableByValue === true) {
          items?.[0]?.makeAnchor();
          items = [ContextMenuItem.filterBy(column.id, this.onFilterChange)].concat(items ?? []);
        }

        if (items === null && this.contextMenuSlot === null) {
          // ни у колонки, ни у строки меню нет
        }
        else {
          this.contextMenuState = {
            visible:  true,
            row,
            items,
          };
        }
      }

      this.$emit('row:contextmenu', row, event);
    },

    // ==================== События групп ====================

    onGroupClick(item: Record<string, unknown>, event: MouseEvent): void {
      if (this.isGroupDisabled(item, item[this.groupChildrenProperty] as Record<string, unknown>[])) {
        return;
      }
      this.$emit('group:click', item, event);
    },

    onGroupDblClick(item: Record<string, unknown>, event: MouseEvent): void {
      if (this.selectable) {
        return;
      }
      if (this.isGroupDisabled(item, item[this.groupChildrenProperty] as Record<string, unknown>[])) {
        return;
      }
      this.$emit('group:dblclick', item, event);
    },

    /** Закрывает ПКМ-меню. Строку не обнуляем — её перезапишет следующее открытие */
    closeContextMenu(): void {
      this.contextMenuState.visible = false;
    },

    // ==================== Helpers ====================

    getHeaderStyle(col: DataGridColumn): Record<string, string | null> {
      return {
        width: col.width,
        textAlign: col.contentAlign,
      };
    },

    /**
     * Классы для <th>. Базовые модификаторы (sortable, sorted) + пользовательский
     * headerClass из VDataGridColumnV2. Тип возврата — массив с примесями (объект + любой ClassValue),
     * Vue нормализует.
     */
    getHeaderClass(col: DataGridColumn): unknown[] {
      return [
        {
          sortable:    col.sortable,
          sorted:      this.sort.by === col.id,
          'with-hint': Boolean(col.hint) && col.hintIconHide === false,
        },
        col.headerClass,
      ];
    },

    selectRow(row: Record<string, unknown>): void {
      const key = row[this.rowKey] as string | number;
      this.localSelectedRows.set(key, row);
      this.$emit('row:selected', row);
    },

    deselectRow(row: Record<string, unknown>): void {
      const key = row[this.rowKey] as string | number;
      this.localSelectedRows.delete(key);
      this.$emit('row:deselected', row);
    },

    /**
     * Включает режим выбора и сразу выбирает строку.
     * Зовётся из контекстного меню (пункт «Выбрать»): пользователь
     * выбирает строку из меню, а грид при этом входит в режим выбора.
     */
    startSelectionWithRow(row: Record<string, unknown>): void {
      this.$emit('update:selectable', true);
      this.onRowSelect(row);
    },

    /**
     * Выключает режим выбора и сбрасывает все выбранные строки.
     * Зовётся из контекстного меню (пункт «Отменить выбор»).
     */
    cancelSelection(): void {
      this.localSelectedRows.clear();
      this.emitSelectedRows();
      this.$emit('update:selectable', false);
    },

    /**
     * Общий конфиг синтетической кебаб-колонки контекстного меню.
     * Отличается только cellSlot — его передаёт вызывающий метод
     * (buildKebabColumn — из пропа, parseContextMenuFromSlot — из слота).
     */
    buildContextMenuColumn(
      cellSlot: (slotProps: { row: Record<string, unknown> }) => VNode[]
    ): DataGridColumn {
      return {
        cellSlot,

        id: '__context_menu__',
        label: '',
        width: '52px',
        contentAlign: 'center',
        contentClass: 'form',
        headerClass: null,
        sortable: false,
        sortDefaultValue: null,
        hint: null,
        filterable: false,
        filterType: 'text',
        filterProps: {},
        filterDefaultValue: null,
        filterSendName: null,
        filterIdentity: null,
        labelSlot: null,
        labelComponent: null,
        footerSlot: null,

        onEdit: null,
        editMaterial: 'edit',
        isFilterableByValue: false,
      };
    },
  },
});
</script>